/**
Copyright (c) 2022-2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  DocumentTextIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  PauseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { containerService } from '../../services/api';

interface ContainerLogsProps {
  containerId: string;
  projectId: string;
  containerName: string;
  isRunning: boolean;
}

interface LogMessage {
  type: 'connected' | 'log' | 'error' | 'end';
  data?: string;
  message?: string;
  error?: string;
  pod?: string;
}

const ContainerLogs: React.FC<ContainerLogsProps> = ({
  containerId,
  projectId,
  containerName,
  isRunning,
}) => {
  const [logs, setLogs] = useState<string[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tailLines, setTailLines] = useState(100);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [podName, setPodName] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const logsContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  // Handle scroll to detect if user scrolled up
  const handleScroll = useCallback(() => {
    if (!logsContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logsContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 50;
    setAutoScroll(isAtBottom);
  }, []);

  // Connect to WebSocket for streaming logs
  const startStreaming = useCallback(() => {
    if (!isRunning) {
      setError('Container must be running to stream logs');
      return;
    }

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    setIsLoading(true);
    setError(null);
    setLogs([]);

    const wsUrl = containerService.getLogsStreamUrl(containerId, projectId, tailLines, true);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsStreaming(true);
        setIsLoading(false);
      };

      ws.onmessage = (event) => {
        try {
          const message: LogMessage = JSON.parse(event.data);

          switch (message.type) {
            case 'connected':
              if (message.pod) {
                setPodName(message.pod);
              }
              break;
            case 'log':
              if (message.data) {
                setLogs((prev) => [...prev, message.data!.trim()]);
              }
              break;
            case 'error':
              setError(message.error || 'Unknown error occurred');
              break;
            case 'end':
              setIsStreaming(false);
              break;
          }
        } catch {
          // If not JSON, treat as plain text log
          setLogs((prev) => [...prev, event.data]);
        }
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
        setIsConnected(false);
        setIsStreaming(false);
        setIsLoading(false);
      };

      ws.onclose = () => {
        setIsConnected(false);
        setIsStreaming(false);
        setIsLoading(false);
      };
    } catch (err) {
      setError('Failed to connect to log stream');
      setIsLoading(false);
    }
  }, [containerId, projectId, tailLines, isRunning]);

  // Stop streaming
  const stopStreaming = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsStreaming(false);
    setIsConnected(false);
  }, []);

  // Fetch logs (non-streaming)
  const fetchLogs = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await containerService.getLogs(containerId, projectId, tailLines);
      const logsData = response.data?.logs || [];
      setLogs(logsData);
      if (response.data?.pod_name) {
        setPodName(response.data.pod_name);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch logs');
    } finally {
      setIsLoading(false);
    }
  }, [containerId, projectId, tailLines]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  // Download logs as file
  const downloadLogs = () => {
    const content = logs.join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${containerName}-logs-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Clear logs
  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <DocumentTextIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-white">Container Logs</h3>
          {isConnected && (
            <span className="flex items-center gap-1 text-xs text-green-500 bg-green-500/20 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
              Live
            </span>
          )}
          {isExpanded ? (
            <ChevronUpIcon className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDownIcon className="w-4 h-4 text-gray-400" />
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Tail lines selector */}
          <select
            value={tailLines}
            onChange={(e) => setTailLines(Number(e.target.value))}
            className="text-sm border border-gray-700 rounded-lg px-2 py-1 bg-gray-800 text-gray-300"
            disabled={isStreaming}
          >
            <option value={50}>50 lines</option>
            <option value={100}>100 lines</option>
            <option value={500}>500 lines</option>
            <option value={1000}>1000 lines</option>
          </select>

          {/* Stream/Stop button */}
          {isRunning && (
            isStreaming ? (
              <button
                onClick={stopStreaming}
                className="flex items-center gap-1 text-sm text-orange-500 hover:text-orange-400 px-2 py-1 rounded-lg hover:bg-orange-500/20"
              >
                <PauseIcon className="w-4 h-4" />
                Stop
              </button>
            ) : (
              <button
                onClick={startStreaming}
                disabled={isLoading}
                className="flex items-center gap-1 text-sm text-green-500 hover:text-green-400 px-2 py-1 rounded-lg hover:bg-green-500/20"
              >
                <PlayIcon className="w-4 h-4" />
                Stream
              </button>
            )
          )}

          {/* Refresh button */}
          <button
            onClick={fetchLogs}
            disabled={isLoading || isStreaming}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-300 px-2 py-1 rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>

          {/* Download button */}
          {logs.length > 0 && (
            <button
              onClick={downloadLogs}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-300 px-2 py-1 rounded-lg hover:bg-gray-800"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Download
            </button>
          )}

          {/* Clear button */}
          {logs.length > 0 && (
            <button
              onClick={clearLogs}
              className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-300 px-2 py-1 rounded-lg hover:bg-gray-800"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <>
          {/* Pod info */}
          {podName && (
            <div className="text-xs text-gray-400 mb-2">
              Pod: <span className="font-mono">{podName}</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-2 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          {/* Not running message */}
          {!isRunning && (
            <div className="bg-yellow-500/20 border border-yellow-500/30 text-yellow-400 px-4 py-2 rounded-lg mb-4 text-sm">
              Container is not running. Start the container to view live logs.
            </div>
          )}

          {/* Logs container */}
          <div
            ref={logsContainerRef}
            onScroll={handleScroll}
            className="bg-gray-900 text-gray-100 font-mono text-sm rounded-lg p-4 h-80 overflow-y-auto border border-gray-800"
          >
            {isLoading && logs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-gray-400">
                <ArrowPathIcon className="w-6 h-6 animate-spin mr-2" />
                Loading logs...
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-400">
                <DocumentTextIcon className="w-12 h-12 mb-2" />
                <p>No logs available</p>
                {isRunning && (
                  <button
                    onClick={startStreaming}
                    className="mt-2 text-green-400 hover:text-green-300 flex items-center gap-1"
                  >
                    <PlayIcon className="w-4 h-4" />
                    Start streaming logs
                  </button>
                )}
              </div>
            ) : (
              <>
                {logs.map((log, index) => (
                  <div
                    key={index}
                    className="whitespace-pre-wrap break-all hover:bg-gray-800 px-1 -mx-1 rounded"
                  >
                    {log}
                  </div>
                ))}
                <div ref={logsEndRef} />
              </>
            )}
          </div>

          {/* Auto-scroll indicator */}
          {logs.length > 0 && !autoScroll && (
            <button
              onClick={() => {
                setAutoScroll(true);
                logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="mt-2 text-xs text-primary-500 hover:text-primary-400 flex items-center gap-1"
            >
              <ChevronDownIcon className="w-3 h-3" />
              Scroll to bottom
            </button>
          )}

          {/* Status bar */}
          <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
            <span>{logs.length} log lines</span>
            {isStreaming && (
              <span className="flex items-center gap-1 text-green-500">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                Streaming...
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ContainerLogs;
